import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";
import { Plus, X, Loader2, Receipt, Send, Trash2, Copy } from "lucide-react";
import EmailStatusBadge from "../components/EmailStatusBadge";

function fmtMoney(v, ccy = "usd") {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: (ccy || "usd").toUpperCase() }).format(Number(v)); }
  catch { return `$${Number(v).toFixed(2)}`; }
}
function fmtDate(iso) { if (!iso) return ""; return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    const [r, c] = await Promise.all([axios.get(`${API_BASE}/invoices`), axios.get(`${API_BASE}/clients`)]);
    setRows(r.data); setClients(c.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "—";

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Billing</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Invoices</h1>
          <p className="text-[var(--ink-2)] mt-2">Draft, send, and collect payment via Stripe Checkout.</p>
        </div>
        <button onClick={() => { setEditing(null); setShow(true); }} className="btn-primary" data-testid="invoice-create-btn">
          <Plus size={14} /> New invoice
        </button>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="invoices-empty">
          <Receipt size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No invoices yet</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>#</th><th>Client</th><th>Total</th><th>Status</th><th>Issued</th><th>Email</th><th></th></tr></thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                  <td className="font-mono">{inv.number}</td>
                  <td>{clientName(inv.client_id)}</td>
                  <td className="font-mono">{fmtMoney(inv.total, inv.currency)}</td>
                  <td>
                    <span className="badge" style={{
                      background: inv.status === "paid" ? "var(--moss)" : inv.status === "sent" ? "var(--accent)" : "var(--ink-3)",
                      color: "var(--paper)"
                    }}>{inv.status}</span>
                  </td>
                  <td className="text-[12px] text-[var(--ink-3)] font-mono">{fmtDate(inv.sent_at || inv.created_at)}</td>
                  <td>
                    <EmailStatusBadge status={inv.last_email_status} testid={`invoice-email-status-${inv.invoice_id}`} />
                  </td>
                  <td className="text-right">
                    <button onClick={() => { setEditing(inv); setShow(true); }}
                            className="text-[12px] text-[var(--accent)] hover:underline" data-testid={`invoice-edit-${inv.invoice_id}`}>
                      Edit →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {show && <InvoiceModal clients={clients} editing={editing}
                             onClose={() => { setShow(false); setEditing(null); }}
                             onSaved={() => { setShow(false); setEditing(null); reload(); }} />}
    </div>
  );
}

function InvoiceModal({ clients, editing, onClose, onSaved }) {
  const blank = { client_id: clients[0]?.client_id || "", currency: "usd", due_at: "", notes: "" };
  const [form, setForm] = useState(editing
    ? { client_id: editing.client_id, currency: editing.currency || "usd",
        due_at: editing.due_at ? editing.due_at.slice(0, 10) : "",
        notes: editing.notes || "" }
    : blank);
  const _uid = () => `ln_${Math.random().toString(36).slice(2, 9)}`;
  const [lines, setLines] = useState(editing?.lines?.length
    ? editing.lines.map((l) => ({ _uid: _uid(), ...l }))
    : [{ _uid: _uid(), description: "", quantity: 1, unit_amount: 0 }]);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const addLine = () => setLines((xs) => [...xs, { _uid: _uid(), description: "", quantity: 1, unit_amount: 0 }]);
  const updLine = (i, k, v) => setLines((xs) => xs.map((x, idx) => idx === i ? { ...x, [k]: k === "description" ? v : Number(v) } : x));
  const rmLine = (i) => setLines((xs) => xs.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((s, l) => s + (Number(l.quantity) * Number(l.unit_amount) || 0), 0);

  const save = async (e) => {
    e?.preventDefault();
    if (!form.client_id || lines.filter((l) => l.description.trim()).length === 0) {
      setErr("Pick a client and add at least one line."); return;
    }
    setBusy(true); setErr("");
    try {
      const payload = {
        client_id: form.client_id, currency: form.currency,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : undefined,
        notes: form.notes || undefined,
        lines: lines.filter((l) => l.description.trim()).map((l) => ({
          description: l.description.trim(), quantity: Number(l.quantity) || 1, unit_amount: Number(l.unit_amount) || 0,
        })),
      };
      let inv;
      if (editing) {
        const r = await axios.patch(`${API_BASE}/invoices/${editing.invoice_id}`, payload);
        inv = r.data;
      } else {
        const r = await axios.post(`${API_BASE}/invoices`, payload);
        inv = r.data;
      }
      toast.success(editing ? "Invoice updated" : `Invoice ${inv.number} created`);
      return inv;
    } catch (e2) {
      setErr(formatApiError(e2));
      return null;
    } finally { setBusy(false); }
  };

  const saveAndSend = async (e) => {
    e.preventDefault();
    const inv = await save();
    if (!inv) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/invoices/${inv.invoice_id}/send`, {});
      setSendResult({ ...r.data, invoice_id: inv.invoice_id });
      toast.success("Invoice sent");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally { setBusy(false); }
  };

  const copyPayLink = async () => {
    const link = sendResult?.pay_url;
    if (!link) return;
    try { await navigator.clipboard.writeText(link); toast.success("Pay link copied"); }
    catch { toast.error("Copy failed: " + link); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,24,20,0.45)" }} data-testid="invoice-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">{editing ? `Edit ${editing.number}` : "New invoice"}</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>
              {editing ? "Edit invoice" : "Compose invoice"}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="invoice-modal-close"><X size={20} /></button>
        </div>

        {sendResult ? (
          <div data-testid="invoice-sent-result">
            <div className="text-[14px] mb-4">Invoice sent.</div>
            <div className="card p-4 mb-4" style={{ background: "var(--subtle)" }}>
              <div className="overline mb-1">Pay link</div>
              <div className="text-[12px] font-mono break-all">{sendResult.pay_url}</div>
              {sendResult.email_result?.ok === false && (
                <div className="text-[12px] text-[var(--accent)] mt-2">
                  Email delivery failed — copy the link and send it manually.
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={copyPayLink} className="btn-secondary flex-1 justify-center" data-testid="invoice-copy-pay-link">
                <Copy size={12} /> Copy pay link
              </button>
              <button onClick={onSaved} className="btn-primary flex-1 justify-center" data-testid="invoice-sent-done">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={save} className="space-y-4" data-testid="invoice-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1.5">Client</label>
                <select value={form.client_id} onChange={(e) => upd("client_id")(e.target.value)} data-testid="invoice-client"
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]">
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-1.5">Due date</label>
                <input type="date" value={form.due_at} onChange={(e) => upd("due_at")(e.target.value)} data-testid="invoice-due"
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Line items</label>
                <button type="button" onClick={addLine} className="text-[12px] text-[var(--accent)] hover:underline" data-testid="invoice-add-line">+ Add line</button>
              </div>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={l._uid} className="flex items-center gap-2" data-testid={`invoice-line-${i}`}>
                    <input
                      value={l.description}
                      onChange={(e) => updLine(i, "description", e.target.value)}
                      placeholder="Description"
                      className="flex-1 bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                    />
                    <input
                      type="number" step="0.01" min="0"
                      value={l.quantity}
                      onChange={(e) => updLine(i, "quantity", e.target.value)}
                      className="w-20 bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] font-mono text-right"
                    />
                    <input
                      type="number" step="0.01" min="0"
                      value={l.unit_amount}
                      onChange={(e) => updLine(i, "unit_amount", e.target.value)}
                      placeholder="0.00"
                      className="w-28 bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] font-mono text-right"
                    />
                    <span className="w-24 text-right text-[13px] font-mono">{fmtMoney(l.quantity * l.unit_amount, form.currency)}</span>
                    <button type="button" onClick={() => rmLine(i)} className="text-[var(--ink-3)] hover:text-[var(--accent)]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-3 pt-3 border-t border-[var(--border)]">
                <div className="text-right">
                  <div className="overline text-[10px]">Subtotal</div>
                  <div className="font-heading text-2xl tabular" data-testid="invoice-subtotal">{fmtMoney(subtotal, form.currency)}</div>
                </div>
              </div>
            </div>

            <div>
              <label className="overline block mb-1.5">Notes (optional)</label>
              <textarea value={form.notes} onChange={(e) => upd("notes")(e.target.value)} data-testid="invoice-notes"
                rows={3} placeholder="e.g. Thank you for your business — Net 14"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[13px]" />
            </div>

            {err && <div className="text-[13px] text-[var(--accent)]" data-testid="invoice-error">{err}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={busy} className="btn-secondary flex-1 justify-center" data-testid="invoice-save">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}{busy ? "Saving…" : "Save draft"}
              </button>
              <button type="button" onClick={saveAndSend} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="invoice-save-send">
                <Send size={14} />{busy ? "Sending…" : "Save & send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
